// src/pages/app/DashboardPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar
} from "recharts";
import { Link } from "react-router-dom";
import {
  fetchDashboardOverview,
  fetchDashboardOverviewRange,
  fetchDashboardConversion,
  DashboardOverviewResponse,
  DashboardOverviewRangeResponse,
  DashboardConversionResponse,
  TopBotActivity
} from "../../api/dashboard";

const LINE_COLORS = [
  "#6366F1", // indigo
  "#EC4899", // pink
  "#10B981", // green
  "#F59E0B", // amber
  "#3B82F6", // blue
  "#8B5CF6", // violet
  "#F97316", // orange
  "#22C55E" // emerald
];

const RANGE_PRESETS = [10, 30, 90];

function getLocaleFromLanguage(language: string | undefined): string {
  const code = (language || "en").split("-")[0];
  switch (code) {
    case "it":
      return "it-IT";
    case "es":
      return "es-ES";
    default:
      return "en-GB";
  }
}

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = useMemo(
    () => getLocaleFromLanguage(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  );

  const [data, setData] = useState<DashboardOverviewResponse | null>(null);
  const [rangeData, setRangeData] =
    useState<DashboardOverviewRangeResponse | null>(null);
  const [conversionData, setConversionData] =
    useState<DashboardConversionResponse | null>(null);

  const [rangeDays, setRangeDays] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [rangeLoading, setRangeLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const res = await fetchDashboardOverview();
        if (mounted) setData(res);
      } catch (err: any) {
        console.error("Failed to load dashboard", err);
        if (mounted) {
          setError(err?.message || t("dashboard.errors.loadFailed"));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadRange = async () => {
      setRangeLoading(true);
      try {
        const [overviewRange, conversion] = await Promise.all([
          fetchDashboardOverviewRange(rangeDays),
          fetchDashboardConversion(rangeDays)
        ]);

        if (!mounted) return;
        setRangeData(overviewRange);
        setConversionData(conversion);
      } catch (err: any) {
        console.error("Failed to load dashboard range data", err);
      } finally {
        if (mounted) setRangeLoading(false);
      }
    };

    loadRange();
    return () => {
      mounted = false;
    };
  }, [rangeDays]);


  const hasBots = (data?.kpis.totalBots ?? 0) > 0;
  const chartSource = rangeData ?? data;
  const kpiSource = rangeData ?? data;
  const hasConvSeries =
    (chartSource?.conversationsLast10Days.series.length ?? 0) > 0;
  const hasTokenSeries =
    (chartSource?.tokensLast10Days.series.length ?? 0) > 0;
  const hasTopBots =
    (chartSource?.topBotsByConversationsLast30Days.length ?? 0) > 0;

  const hasTokenSplit =
    chartSource?.tokenBreakdownLast10Days?.dates.length ? true : false;

  const hasTokenSplitByBot =
    chartSource?.tokenBreakdownByBotLast10Days &&
    chartSource.tokenBreakdownByBotLast10Days.length > 0;

  const tOr = (
    key: string,
    fallback: string,
    options?: Record<string, any>
  ): string => {
    const anyI18n = i18n as any;
    if (typeof anyI18n?.exists === "function" && anyI18n.exists(key)) {
      return t(key, options);
    }
    return fallback;
  };

  const formatPct = (v: number | null | undefined): string => {
    if (v == null || Number.isNaN(v)) return "—";
    return `${v.toFixed(1)}%`;
  };

  // UI-only: show tokens/10 (raw stays in API + backend math)
  const tokensThisMonthUi = kpiSource
    ? Math.round(kpiSource.kpis.totalTokensThisMonth / 10)
    : 0;
  const tokensLimitUi =
    kpiSource?.kpis.monthlyTokensLimit == null
      ? null
      : Math.round(kpiSource.kpis.monthlyTokensLimit / 10);

  const tokensPlanHint = useMemo(() => {
    if (!kpiSource) return "";
    if (kpiSource.kpis.monthlyTokensLimit == null) {
      return tOr("dashboard.kpisHints.unlimitedPlan", "Unlimited plan");
    }
    if (kpiSource.kpis.monthlyTokensLimit <= 0) return "";
    const limit =
      tokensLimitUi ?? Math.round(kpiSource.kpis.monthlyTokensLimit / 10);
    return `${tOr("dashboard.kpisHints.of", "of")} ${limit.toLocaleString(
      locale
    )}`;
  }, [kpiSource, locale, tokensLimitUi, i18n, t]);

  const emailsPlanHint = useMemo(() => {
    if (!kpiSource) return "";
    if (kpiSource.kpis.monthlyEmailsLimit == null) {
      return tOr("dashboard.kpisHints.unlimitedPlan", "Unlimited plan");
    }
    if (kpiSource.kpis.monthlyEmailsLimit <= 0) return "";
    return `${tOr(
      "dashboard.kpisHints.of",
      "of"
    )} ${kpiSource.kpis.monthlyEmailsLimit.toLocaleString(locale)}`;
  }, [kpiSource, locale, i18n, t]);

  // Helpers for token UI formatting (keep raw numbers under the hood)
  const formatTokenTick = (value: number): string =>
    Math.round(value / 10).toLocaleString(locale);

  const formatTokenTooltipValue = (
    value: number | string,
    name: string
  ): [string, string] => {
    const num = typeof value === "number" ? value : Number(value) || 0;
    const ui = Math.round(num / 10).toLocaleString(locale);
    return [ui, name];
  };

  const formatDelta = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return "—";
    const prefix = value > 0 ? "+" : "";
    return `${prefix}${value.toFixed(1)}%`;
  };

  const totalConversationsInRange = useMemo(() => {
    if (!chartSource || !chartSource.conversationsLast10Days?.series) return 0;
    return chartSource.conversationsLast10Days.series.reduce((sum, s) => {
      const seriesSum = (s.values || []).reduce((a, b) => a + (b || 0), 0);
      return sum + seriesSum;
    }, 0);
  }, [chartSource]);

  const formatAlertValue = (alert: { metric: string; value: number }): string => {
    if (alert.metric === "tokens") {
      return Math.round(alert.value / 10).toLocaleString(locale);
    }
    return alert.value.toLocaleString(locale);
  };

  const formatAlertPrevValue = (alert: { metric: string; previousValue: number }): string => {
    if (alert.metric === "tokens") {
      return Math.round(alert.previousValue / 10).toLocaleString(locale);
    }
    return alert.previousValue.toLocaleString(locale);
  };

  // ---- Chart data transforms ----
  const conversationsChartData = useMemo(() => {
    if (!chartSource || !hasConvSeries) return [];
    const { dates, series } = chartSource.conversationsLast10Days;

    return dates.map((date, idx) => {
      const entry: Record<string, any> = { date };
      series.forEach((s) => {
        entry[s.botName] = s.values[idx] ?? 0;
      });
      return entry;
    });
  }, [chartSource, hasConvSeries]);

  const tokensChartData = useMemo(() => {
    if (!chartSource || !hasTokenSeries) return [];
    const { dates, series } = chartSource.tokensLast10Days;

    // IMPORTANT: keep raw values here, only format in axis/tooltip
    return dates.map((date, idx) => {
      const entry: Record<string, any> = { date };
      series.forEach((s) => {
        entry[s.botName] = s.values[idx] ?? 0;
      });
      return entry;
    });
  }, [chartSource, hasTokenSeries]);

  const tokenSplitChartData = useMemo(() => {
    if (!chartSource || !hasTokenSplit) return [];
    const { dates, openAiTokens, emailTokens, whatsappTokens } =
      chartSource.tokenBreakdownLast10Days;

    // Raw token values; formatted in axis/tooltip
    return dates.map((date, idx) => ({
      date,
      openAi: openAiTokens[idx] ?? 0,
      emails: emailTokens[idx] ?? 0,
      whatsapp: whatsappTokens[idx] ?? 0
    }));
  }, [chartSource, hasTokenSplit]);

  // NEW: per-bot breakdown chart data (aggregated over last 10 days)
  const tokenSplitByBotChartData = useMemo(() => {
    if (!chartSource || !hasTokenSplitByBot) return [];
    return chartSource.tokenBreakdownByBotLast10Days.map((row) => ({
      botName: row.botName,
      openAi: row.openAiTokens,
      emails: row.emailTokens,
      whatsapp: row.whatsappTokens
    }));
  }, [chartSource, hasTokenSplitByBot]);

  const topBotsChartData = useMemo(() => {
    if (!chartSource || !hasTopBots) return [];
    return chartSource.topBotsByConversationsLast30Days.map((b: TopBotActivity) => ({
      name: b.botName,
      conversations: b.conversationCount
    }));
  }, [chartSource, hasTopBots]);

  return (
    <div className="page-container dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t("dashboard.title")}</h1>
          <p className="page-subtitle">{t("dashboard.subtitle")}</p>
        </div>

        {hasBots && (
          <Link to="/onboarding/bots/new" className="btn-primary">
            {t("dashboard.actions.createNewBot")}
          </Link>
        )}
      </div>

      {!loading && !error && hasBots && (
        <div className="dashboard-range-row">
          <div className="dashboard-range-left">
            <span className="dashboard-range-label">
              {t("dashboard.range.label", "Time range")}
            </span>
            <div className="dashboard-range-pills">
              {RANGE_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={
                    "dashboard-range-pill" +
                    (rangeDays === d ? " dashboard-range-pill--active" : "")
                  }
                  onClick={() => setRangeDays(d)}
                >
                  {t("dashboard.range.last", "Last")} {d}{" "}
                  {t("dashboard.range.days", "days")}
                </button>
              ))}
            </div>
          </div>
          <div className="dashboard-range-hint">
            {rangeLoading
              ? t("dashboard.range.loading", "Updating charts…")
              : t("dashboard.range.hint", "Charts update based on this range.")}
          </div>
        </div>
      )}

      {loading && (
        <div className="card">
          <p>{t("dashboard.loading")}</p>
        </div>
      )}

      {!loading && error && (
        <div className="card card-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && !hasBots && (
        <div className="card">
          <h2 className="card-title">{t("dashboard.empty.title")}</h2>
          <p className="card-subtitle">{t("dashboard.empty.subtitle")}</p>
          <div style={{ marginTop: "1rem" }}>
            <Link to="/onboarding/bots/new" className="btn-primary">
              {t("dashboard.empty.cta")}
            </Link>
          </div>
        </div>
      )}

      {!loading && !error && hasBots && data && (
        <>
          {/* KPI cards */}
<section className="dashboard-section">
  {/* Row 1 – bots & conversations */}
  <div className="dashboard-kpi-row dashboard-kpi-row--top">
    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-label">
        {t("dashboard.kpis.totalBots")}
      </div>
      <div className="dashboard-kpi-value">
        {kpiSource?.kpis.totalBots ?? data.kpis.totalBots}
      </div>
      <div className="dashboard-kpi-hint">
        {t("dashboard.kpisHints.totalBots")}
      </div>
    </div>

    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-label">
        {t("dashboard.kpis.activeBots")}
      </div>
      <div className="dashboard-kpi-value">
        {kpiSource?.kpis.activeBots ?? data.kpis.activeBots}
      </div>
      <div className="dashboard-kpi-hint">
        {t("dashboard.kpisHints.activeBots")}
      </div>
    </div>

    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-label">
        {t("dashboard.kpis.conversationsLast30Days", { days: rangeDays })}
      </div>
      <div className="dashboard-kpi-value">
        {kpiSource?.kpis.totalConversationsLast30Days ??
          data.kpis.totalConversationsLast30Days}
      </div>
      <div className="dashboard-kpi-hint">
        {t("dashboard.kpisHints.conversationsLast30Days")}
      </div>
    </div>
  </div>

  {/* Row 2 – energy, emails, WhatsApp messages */}
  <div className="dashboard-kpi-row dashboard-kpi-row--bottom">
    {/* Energy (tokens) */}
    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-label">
        {t("dashboard.kpis.tokensThisMonth", { days: rangeDays })}
      </div>

      <div className="dashboard-kpi-value-row">
        <div className="dashboard-kpi-value">
          {tokensThisMonthUi.toLocaleString(locale)}
        </div>
        {kpiSource?.kpis.tokensUsagePercent != null && (
          <span className="dashboard-kpi-pill">
            {formatPct(kpiSource.kpis.tokensUsagePercent)}
          </span>
        )}
      </div>

      <div className="dashboard-kpi-hint">{tokensPlanHint}</div>
    </div>

    {/* Emails */}
    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-label">
        {tOr(
          "dashboard.kpis.emailsThisMonth",
          `Emails sent (last ${rangeDays} days)`,
          { days: rangeDays }
        )}
      </div>
      <div className="dashboard-kpi-value-row">
        <div className="dashboard-kpi-value">
          {(kpiSource?.kpis.totalEmailsThisMonth ??
            data.kpis.totalEmailsThisMonth
          ).toLocaleString(locale)}
        </div>
        {kpiSource?.kpis.emailsUsagePercent != null && (
          <span className="dashboard-kpi-pill">
            {formatPct(kpiSource.kpis.emailsUsagePercent)}
          </span>
        )}
      </div>
      <div className="dashboard-kpi-hint">{emailsPlanHint}</div>
    </div>

    {/* WhatsApp messages / leads */}
    <div className="dashboard-kpi-card">
      <div className="dashboard-kpi-label">
        {tOr(
          "dashboard.kpis.whatsappLeadsThisMonth",
          `WhatsApp leads (last ${rangeDays} days)`,
          { days: rangeDays }
        )}
      </div>
      <div className="dashboard-kpi-value">
        {(kpiSource?.kpis.totalWhatsappLeadsThisMonth ??
          data.kpis.totalWhatsappLeadsThisMonth
        ).toLocaleString(locale)}
      </div>
      <div className="dashboard-kpi-hint">
        {tOr(
          "dashboard.kpisHints.whatsappLeadsThisMonth",
          `Number of WhatsApp lead templates sent in the last ${rangeDays} days`,
          { days: rangeDays }
        )}
      </div>
    </div>
  </div>
</section>

          {totalConversationsInRange === 0 && (
            <section className="dashboard-section">
              <div className="card dashboard-coaching-card">
                <h2>{t("dashboard.coaching.title", "Get your dashboard moving")}</h2>
                <p className="card-subtitle">
                  {t(
                    "dashboard.coaching.subtitle",
                    "No conversations yet in this range. Here are quick ways to get activity."
                  )}
                </p>
                <div className="dashboard-coaching-actions">
                  <Link to="/onboarding/bots/new" className="btn-primary">
                    {t("dashboard.coaching.createBot", "Create a bot")}
                  </Link>
                  <Link to="/app/bots" className="btn-secondary">
                    {t("dashboard.coaching.connectChannels", "Connect channels")}
                  </Link>
                  <Link to="/app/bots" className="btn-secondary">
                    {t("dashboard.coaching.addKnowledge", "Add knowledge")}
                  </Link>
                </div>
              </div>
            </section>
          )}

          <section className="dashboard-section">
            <div className="dashboard-conversion-card card">
              <div className="card-header">
                <h2>{t("dashboard.conversion.title", "User journey")}</h2>
                <p className="card-subtitle">
                  {t(
                    "dashboard.conversion.subtitle",
                    "Conversations → leads → bookings"
                  )}
                </p>
              </div>

              {conversionData ? (
                <>
                  <div className="dashboard-conversion-grid">
                    <div>
                      <div className="dashboard-conversion-value">
                        {conversionData.totals.conversations.toLocaleString(locale)}
                      </div>
                      <div className="dashboard-conversion-label">
                        {t("dashboard.conversion.conversations", "Conversations")}
                      </div>
                    </div>
                    <div>
                      <div className="dashboard-conversion-value">
                        {conversionData.totals.leads.toLocaleString(locale)}
                      </div>
                      <div className="dashboard-conversion-label">
                        {t("dashboard.conversion.leads", "Leads")}
                      </div>
                      <div className="dashboard-conversion-rate">
                        {conversionData.totals.conversations > 0
                          ? `${conversionData.totals.leadRate.toFixed(1)}%`
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="dashboard-conversion-value">
                        {conversionData.totals.bookings.toLocaleString(locale)}
                      </div>
                      <div className="dashboard-conversion-label">
                        {t("dashboard.conversion.bookings", "Bookings")}
                      </div>
                      <div className="dashboard-conversion-rate">
                        {conversionData.totals.leads > 0 ||
                        conversionData.totals.conversations > 0 ||
                        conversionData.totals.bookings > 0
                          ? `${conversionData.totals.bookingRate.toFixed(1)}%`
                          : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="dashboard-conversion-footer">
                    <Link to="/app/dashboard/conversion" className="btn-link-small">
                      {t("dashboard.conversion.viewDetails", "View per-bot breakdown")}
                    </Link>
                  </div>
                </>
              ) : (
                <p className="card-empty">
                  {t("dashboard.conversion.empty", "Conversion data not available.")}
                </p>
              )}
            </div>
          </section>

          {/* Conversations / Tokens charts */}
          <section className="dashboard-section">
            <div className="dashboard-grid">
              <div className="card chart-card">
                <div className="card-header">
                  <h2>
                    {t("dashboard.charts.conversationsLast10Days.title", {
                      days: rangeDays
                    })}
                  </h2>
                  <p className="card-subtitle">
                    {t("dashboard.charts.conversationsLast10Days.subtitle")}
                    <span className="dashboard-range-tag">
                      • {t("dashboard.range.last", "Last")} {rangeDays}{" "}
                      {t("dashboard.range.days", "days")}
                    </span>
                  </p>
                </div>

                {hasConvSeries ? (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <LineChart data={conversationsChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(17,24,39,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10
                          }}
                          labelStyle={{ color: "#9ca3af" }}
                        />
                        <Legend />
                        {chartSource?.conversationsLast10Days.series.map(
                          (s, idx) => (
                            <Line
                              key={s.botId}
                              type="monotone"
                              dataKey={s.botName}
                              stroke={
                                LINE_COLORS[idx % LINE_COLORS.length]
                              }
                              strokeWidth={2}
                              dot={false}
                            />
                          )
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="card-empty">
                    {t("dashboard.charts.conversationsLast10Days.empty")}
                  </p>
                )}
              </div>

              <div className="card chart-card">
                <div className="card-header">
                  <h2>
                    {t("dashboard.charts.tokensLast10Days.title", {
                      days: rangeDays
                    })}
                  </h2>
                  <p className="card-subtitle">
                    {t("dashboard.charts.tokensLast10Days.subtitle")}
                    <span className="dashboard-range-tag">
                      • {t("dashboard.range.last", "Last")} {rangeDays}{" "}
                      {t("dashboard.range.days", "days")}
                    </span>
                  </p>
                </div>

                {hasTokenSeries ? (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <LineChart data={tokensChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis
                          allowDecimals={false}
                          tickFormatter={formatTokenTick}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(17,24,39,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10
                          }}
                          labelStyle={{ color: "#9ca3af" }}
                          formatter={formatTokenTooltipValue}
                        />
                        <Legend />
                        {chartSource?.tokensLast10Days.series.map((s, idx) => (
                          <Line
                            key={s.botId}
                            type="monotone"
                            dataKey={s.botName}
                            stroke={
                              LINE_COLORS[idx % LINE_COLORS.length]
                            }
                            strokeWidth={2}
                            dot={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="card-empty">
                    {t("dashboard.charts.tokensLast10Days.empty")}
                  </p>
                )}
              </div>

              {/* Existing split by channel over time */}
              <div className="card chart-card">
                <div className="card-header">
                  <h2>
                    {tOr(
                      "dashboard.charts.tokensSplitLast10Days.title",
                      `Tokens by channel (last ${rangeDays} days)`,
                      { days: rangeDays }
                    )}
                  </h2>
                  <p className="card-subtitle">
                    {tOr(
                      "dashboard.charts.tokensSplitLast10Days.subtitle",
                      "OpenAI + knowledge vs emails vs WhatsApp templates"
                    )}
                    <span className="dashboard-range-tag">
                      • {t("dashboard.range.last", "Last")} {rangeDays}{" "}
                      {t("dashboard.range.days", "days")}
                    </span>
                  </p>
                </div>

                {hasTokenSplit ? (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={tokenSplitChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis
                          allowDecimals={false}
                          tickFormatter={formatTokenTick}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(17,24,39,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10,
                            padding: "0.45rem 0.7rem",
                            fontSize: "0.8rem"
                          }}
                          labelStyle={{ color: "#9ca3af" }}
                          formatter={formatTokenTooltipValue}
                        />
                        <Legend />
                        {/* OpenAI + Knowledge */}
                        <Bar
                          dataKey="openAi"
                          stackId="tokens"
                          fill="#6366F1"
                          name={tOr(
                            "dashboard.tokensSplit.legend.openAi",
                            "OpenAI + knowledge"
                          )}
                          radius={[6, 6, 0, 0]}
                        />
                        {/* Emails (virtual tokens) */}
                        <Bar
                          dataKey="emails"
                          stackId="tokens"
                          fill="#F97316"
                          name={tOr(
                            "dashboard.tokensSplit.legend.emails",
                            "Emails"
                          )}
                        />
                        {/* WhatsApp lead templates */}
                        <Bar
                          dataKey="whatsapp"
                          stackId="tokens"
                          fill="#22C55E"
                          name={tOr(
                            "dashboard.tokensSplit.legend.whatsapp",
                            "WhatsApp templates"
                          )}
                          radius={[0, 0, 6, 6]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="card-empty">
                    {tOr(
                      "dashboard.charts.tokensSplitLast10Days.empty",
                      "Not enough data yet to show the split."
                    )}
                  </p>
                )}
              </div>

              {/* NEW: per-bot breakdown by channel */}
              <div className="card chart-card">
                <div className="card-header">
                  <h2>
                    {tOr(
                      "dashboard.charts.tokensSplitByBotLast10Days.title",
                      `Tokens by bot & channel (last ${rangeDays} days)`,
                      { days: rangeDays }
                    )}
                  </h2>
                  <p className="card-subtitle">
                    {tOr(
                      "dashboard.charts.tokensSplitByBotLast10Days.subtitle",
                      "How each bot consumes tokens via OpenAI + knowledge, emails, and WhatsApp templates"
                    )}
                    <span className="dashboard-range-tag">
                      • {t("dashboard.range.last", "Last")} {rangeDays}{" "}
                      {t("dashboard.range.days", "days")}
                    </span>
                  </p>
                </div>

                {hasTokenSplitByBot ? (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={tokenSplitByBotChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="botName" />
                        <YAxis
                          allowDecimals={false}
                          tickFormatter={formatTokenTick}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(17,24,39,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10,
                            padding: "0.45rem 0.7rem",
                            fontSize: "0.8rem"
                          }}
                          labelStyle={{ color: "#9ca3af" }}
                          formatter={formatTokenTooltipValue}
                        />
                        <Legend />
                        <Bar
                          dataKey="openAi"
                          stackId="tokens"
                          fill="#6366F1"
                          name={tOr(
                            "dashboard.tokensSplit.legend.openAi",
                            "OpenAI + knowledge"
                          )}
                          radius={[6, 6, 0, 0]}
                        />
                        <Bar
                          dataKey="emails"
                          stackId="tokens"
                          fill="#F97316"
                          name={tOr(
                            "dashboard.tokensSplit.legend.emails",
                            "Emails"
                          )}
                        />
                        <Bar
                          dataKey="whatsapp"
                          stackId="tokens"
                          fill="#22C55E"
                          name={tOr(
                            "dashboard.tokensSplit.legend.whatsapp",
                            "WhatsApp templates"
                          )}
                          radius={[0, 0, 6, 6]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="card-empty">
                    {tOr(
                      "dashboard.charts.tokensSplitByBotLast10Days.empty",
                      "Not enough data yet to show per-bot split."
                    )}
                  </p>
                )}
              </div>

              <div className="card chart-card">
                <div className="card-header">
                  <h2>{t("dashboard.charts.topBots.title")}</h2>
                  <p className="card-subtitle">
                    {t("dashboard.charts.topBots.subtitle")}
                    <span className="dashboard-range-tag">
                      â€¢ {t("dashboard.range.last", "Last")} {rangeDays}{" "}
                      {t("dashboard.range.days", "days")}
                    </span>
                  </p>
                </div>

                {hasTopBots ? (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={topBotsChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(17,24,39,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10,
                            padding: "0.45rem 0.7rem",
                            fontSize: "0.8rem"
                          }}
                          labelStyle={{ color: "#9ca3af" }}
                        />
                        <Bar
                          dataKey="conversations"
                          fill="#6366F1"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="card-empty">
                    {t("dashboard.charts.topBots.empty")}
                  </p>
                )}
              </div>

              <div className="card chart-card">
                <div className="card-header">
                  <h2>{t("dashboard.activity.title")}</h2>
                  <p className="card-subtitle">
                    {t("dashboard.activity.subtitle")}
                  </p>
                </div>

                {hasTopBots ? (
                  <ul className="list-plain">
                    {chartSource?.topBotsByConversationsLast30Days.map((bot) => (
                      <li key={bot.botId} className="list-row">
                        <div className="list-row-main">
                          <div className="list-title">{bot.botName}</div>
                          {bot.lastConversationAt && (
                            <div className="list-subtitle">
                              {t("dashboard.activity.lastConversation")}{" "}
                              {new Date(
                                bot.lastConversationAt
                              ).toLocaleString(locale)}
                            </div>
                          )}
                        </div>

                        <div className="list-row-meta">
                          <span className="badge">
                            {bot.conversationCount}{" "}
                            {t("dashboard.activity.convAbbrev")}
                          </span>
                          <Link
                            to={`/app/bots/${bot.botId}/conversations`}
                            className="btn-link-small"
                          >
                            {t("dashboard.activity.view")}
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="card-empty">
                    {t("dashboard.activity.empty")}
                  </p>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
